import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminAccountsView from "@/components/apik/AdminAccountsView";
import PpidAccountsView from "@/components/apik/PpidAccountsView";
import PasswordInput from "@/components/PasswordInput";

const cardCls =
  "rounded-3xl border border-[#E3EBF3] bg-white p-6 md:p-8";

export default function AkunView({ user }) {
  const { checkAuth } = useAuth();
  const [pin, setPin] = useState("");
  const [username, setUsername] = useState(user?.username || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState("");
  const [showCredForm, setShowCredForm] = useState(false);

  const savePin = async () => {
    setSaving("pin");
    try {
      await api("/api/account/pin", {
        method: "POST",
        body: JSON.stringify({ pin }),
      });
      setPin("");
      toast("PIN berhasil diperbarui.");
      await checkAuth();
    } catch (err) {
      toast(formatApiError(err.message));
    } finally {
      setSaving("");
    }
  };

  const saveCredentials = async () => {
    setSaving("cred");
    try {
      await api("/api/account/credentials", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      setPassword("");
      setShowCredForm(false);
      toast("Login username & password berhasil disimpan.");
      await checkAuth();
    } catch (err) {
      toast(formatApiError(err.message));
    } finally {
      setSaving("");
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold text-[#0B2440] md:text-4xl">
        Akun
      </h1>
      <p className="mt-3 text-sm text-slate-500">
        Akun Anda:{" "}
        <span className="font-semibold text-[#1479BF]">{user?.label}</span>.
        Kelola cara login di bawah ini.
      </p>

      <div
        className={`mt-8 grid gap-6 ${
          user?.role === "pengelola" ? "md:grid-cols-2" : ""
        }`}
      >
        {user?.role === "pengelola" && (
          <section className={cardCls}>
            <h2 className="font-display text-xl font-semibold text-[#0B2440]">
              Ubah PIN
            </h2>
          <p
            data-testid="apik-pin-status"
            className={`mt-2 text-xs font-semibold ${
              user?.has_pin ? "text-emerald-600" : "text-slate-400"
            }`}
          >
            {user?.has_pin ? "PIN sudah terpasang" : "PIN belum ditetapkan"}
          </p>
          <PasswordInput
            inputMode="numeric"
            autoComplete="new-password"
            testid="apik-pin-input"
            placeholder="PIN baru (angka, min. 4 digit)"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="mt-4 w-full rounded-xl border border-[#E3EBF3] px-4 py-3 text-sm outline-none transition focus:border-[#1479BF]"
          />
          <button
            data-testid="apik-pin-save"
            disabled={saving === "pin" || !pin.trim()}
            onClick={savePin}
            className="mt-4 rounded-full bg-[#1479BF] px-6 py-2.5 text-xs font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-[#0B2440] disabled:opacity-50"
          >
            {saving === "pin" ? "MENYIMPAN..." : "SIMPAN PIN"}
          </button>
          <p className="mt-3 text-xs leading-relaxed text-slate-400">
            PIN dipakai untuk masuk melalui halaman Login (login dengan PIN).
          </p>
          </section>
        )}

        <section className={cardCls}>
          <h2 className="font-display text-xl font-semibold text-[#0B2440]">
            Login Username &amp; Password
          </h2>
          <p
            data-testid="apik-cred-status"
            className={`mt-2 text-xs font-semibold ${
              user?.has_credentials ? "text-emerald-600" : "text-slate-400"
            }`}
          >
            {user?.has_credentials
              ? `Aktif — username: ${user.username}`
              : "Belum diaktifkan"}
          </p>
          <p className="mt-3 text-xs leading-relaxed text-slate-500">
            Kalau mau, Anda bisa menggunakan username dan password untuk login
          </p>
          {showCredForm ? (
            <div className="mt-4">
              <input
                data-testid="apik-username-input"
                placeholder="Username (tanpa spasi)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-xl border border-[#E3EBF3] px-4 py-3 text-sm outline-none transition focus:border-[#1479BF]"
              />
              <PasswordInput
                autoComplete="new-password"
                testid="apik-password-input"
                placeholder={
                  user?.has_credentials
                    ? "Password baru (min. 6 karakter)"
                    : "Password (min. 6 karakter)"
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-3 w-full rounded-xl border border-[#E3EBF3] px-4 py-3 text-sm outline-none transition focus:border-[#1479BF]"
              />
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  data-testid="apik-cred-save"
                  disabled={saving === "cred" || !username.trim() || !password}
                  onClick={saveCredentials}
                  className="rounded-full bg-[#1479BF] px-6 py-2.5 text-xs font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-[#0B2440] disabled:opacity-50"
                >
                  {saving === "cred" ? "MENYIMPAN..." : "SIMPAN USERNAME & PASSWORD"}
                </button>
                <button
                  data-testid="apik-cred-cancel"
                  onClick={() => setShowCredForm(false)}
                  className="rounded-full border border-[#E3EBF3] px-5 py-2.5 text-xs font-bold tracking-[0.08em] text-slate-600 transition-colors duration-300 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                >
                  BATAL
                </button>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-slate-400">
                Setelah diaktifkan, Anda dapat masuk dengan username &amp;
                password. Login dengan PIN juga tetap berlaku.
              </p>
            </div>
          ) : (
            <button
              data-testid="apik-cred-activate"
              onClick={() => setShowCredForm(true)}
              className="mt-4 rounded-full bg-[#1479BF] px-6 py-2.5 text-xs font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-[#0B2440]"
            >
              AKTIFKAN USERNAME DAN PASSWORD
            </button>
          )}
        </section>
      </div>

      {user?.role === "ppid" && (
        <>
          <PpidAccountsView user={user} />
          <AdminAccountsView />
        </>
      )}
    </div>
  );
}
